// Who is about to lay down their arms, and how long they have.
//
// A pure model, like `marchView` and `pieceBadges`, and it exists for the same
// reason a standing order is drawn on the board: **an army in despair is
// something the game is doing to a side whether or not anybody is looking.**
// It ran in complete silence — a side was diagnosed, given three turns, and
// swept into scrap, and the only thing on screen that ever changed was the
// army disappearing. When the side is the *player's* that is the run ending
// with no warning and no stated reason.
//
// The reasoning lives in `ai/depression.ts`; the rules hold only the stamp.
// This reads the stamp, so it cannot disagree with what will actually happen —
// the countdown here and the `surrender` gate in the reducer are the same
// subtraction against the same two numbers.

import type { Color, Side } from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import { DESPAIR_NONE, livingSides } from "src/engine/rules/position";
import { despairTurnsLeft } from "src/engine/rules/campaign/despair";

export interface Despairing {
  side: Side;
  /** Ours, so the copy can say "your army" and mean it. */
  mine: boolean;
  /**
   * Turns left before the army lays down its arms, never below zero.
   *
   * The same subtraction `surrender` is gated on, so the number on screen is
   * the number the reducer will act on rather than a second count of it.
   */
  turnsLeft: number;
}

/**
 * Every army currently under the diagnosis, in side order.
 *
 * An enemy's is worth showing too, and not only as courtesy: an army that is
 * about to give up is a fight you have already won, and knowing that is the
 * difference between pressing an attack and walking away from one.
 */
export function despairView(state: GameState, localSide: Color): Despairing[] {
  const out: Despairing[] = [];
  for (const side of livingSides(state.position)) {
    const at = state.position.despair[side] ?? DESPAIR_NONE;
    // Below zero is either "not despairing" or "exempt for good"; neither is
    // something happening to an army, so neither is something to draw.
    if (at < 0) continue;
    out.push({
      side,
      mine: side === localSide,
      // The run's own grace period (§26 F13), not the constant: Hard Mode
      // makes it nought, and a banner promising a turn the reducer will not
      // give is worse than no banner — one subtraction, three readers.
      turnsLeft: despairTurnsLeft(state, side) ?? 0
    });
  }
  return out;
}

/**
 * The one to say out loud, or undefined.
 *
 * Yours first, always. Two armies can be despairing at once — a stage where
 * both sides have been ground down — and the one that is about to end *your*
 * run is not the one that happens to have the lower side number.
 */
export const worstDespair = (all: readonly Despairing[]): Despairing | undefined =>
  all.find((one) => one.mine) ?? all[0];
